import { useQuery } from '@tanstack/react-query'
import { FileSpreadsheet, FileText, Download, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { supabase, isDemoMode } from '@/lib/supabase'
import { DEMO_PARCELS, DEMO_ACTIVITY_INSTANCES, DEMO_PROGRESS_BY_DISCIPLINE } from '@/lib/demoData'
import ExportButton, { exportToExcel, exportToPDF } from '@/components/shared/ExportButton'
import { ProgressCell } from '@/components/shared/ProgressBar'
import { getDisciplineInfo } from '@/lib/constants'
import type { DisciplineType } from '@/types'

// =============================================================================
// ReportsPage
// =============================================================================

export default function ReportsPage() {
  // ---------------------------------------------------------------------------
  // Fetch data for reports
  // ---------------------------------------------------------------------------
  const { data: parcelData, isLoading: parcelLoading } = useQuery({
    queryKey: ['report-parcel-summary'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_PARCELS.map(p => ({
        parcel_number: p.parcel_number,
        display_name:  p.display_name,
        reported_pct:  p.progress_pct ?? 0,
        validated_pct: Math.round((p.progress_pct ?? 0) * 0.85),
        total:         6,
        finalizada:    Math.floor(((p.progress_pct ?? 0) / 100) * 6),
        bloqueada:     (p.progress_pct ?? 0) < 10 ? 0 : 1,
      })).sort((a, b) => a.parcel_number - b.parcel_number)

      const { data } = await supabase
        .from('activity_instances')
        .select(`
          progress_pct, validated_pct, status,
          parcel:parcels(parcel_number, display_name),
          family:activity_families(discipline)
        `)
        .eq('is_active', true)
        .not('parcel_id', 'is', null)

      // Group by parcel
      const grouped: Record<string, {
        parcel_number: number
        display_name:  string
        sum_rep:       number
        sum_val:       number
        count:         number
        finalizada:    number
        bloqueada:     number
      }> = {}

      for (const row of (data ?? [])) {
        const p = row.parcel as { parcel_number: number; display_name: string } | null
        if (!p) continue
        const key = String(p.parcel_number)
        if (!grouped[key]) {
          grouped[key] = {
            parcel_number: p.parcel_number,
            display_name:  p.display_name,
            sum_rep: 0, sum_val: 0, count: 0, finalizada: 0, bloqueada: 0,
          }
        }
        grouped[key].sum_rep  += row.progress_pct
        grouped[key].sum_val  += row.validated_pct
        grouped[key].count    += 1
        if (row.status === 'FINALIZADA') grouped[key].finalizada += 1
        if (row.status === 'BLOQUEADA')  grouped[key].bloqueada  += 1
      }

      return Object.values(grouped)
        .map((g) => ({
          parcel_number:  g.parcel_number,
          display_name:   g.display_name,
          reported_pct:   g.count > 0 ? Math.round(g.sum_rep  / g.count * 10) / 10 : 0,
          validated_pct:  g.count > 0 ? Math.round(g.sum_val  / g.count * 10) / 10 : 0,
          total:          g.count,
          finalizada:     g.finalizada,
          bloqueada:      g.bloqueada,
        }))
        .sort((a, b) => a.parcel_number - b.parcel_number)
    },
    staleTime: 60_000,
  })

  const { data: disciplineData, isLoading: discLoading } = useQuery({
    queryKey: ['report-discipline-summary'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_PROGRESS_BY_DISCIPLINE.map(d => ({
        discipline:   d.discipline,
        reported_pct: d.pct,
        validated_pct: Math.round(d.pct * 0.85),
        total:         d.total,
        finalizada:    d.completed,
        bloqueada:     d.blocked,
      }))
      const { data } = await supabase
        .from('activity_instances')
        .select(`
          progress_pct, validated_pct, status,
          family:activity_families(discipline)
        `)
        .eq('is_active', true)

      const grouped: Record<string, {
        discipline: string
        sum_rep: number
        sum_val: number
        count: number
        finalizada: number
        bloqueada: number
      }> = {}

      for (const row of (data ?? [])) {
        const disc = (row.family as { discipline: string } | null)?.discipline ?? 'Unknown'
        if (!grouped[disc]) {
          grouped[disc] = { discipline: disc, sum_rep: 0, sum_val: 0, count: 0, finalizada: 0, bloqueada: 0 }
        }
        grouped[disc].sum_rep  += row.progress_pct
        grouped[disc].sum_val  += row.validated_pct
        grouped[disc].count    += 1
        if (row.status === 'FINALIZADA') grouped[disc].finalizada += 1
        if (row.status === 'BLOQUEADA')  grouped[disc].bloqueada  += 1
      }

      return Object.values(grouped).map((g) => ({
        discipline:    g.discipline,
        reported_pct:  g.count > 0 ? Math.round(g.sum_rep / g.count * 10) / 10 : 0,
        validated_pct: g.count > 0 ? Math.round(g.sum_val / g.count * 10) / 10 : 0,
        total:         g.count,
        finalizada:    g.finalizada,
        bloqueada:     g.bloqueada,
      }))
    },
    staleTime: 60_000,
  })

  const today = format(new Date(), 'dd/MM/yyyy')

  // ---------------------------------------------------------------------------
  // Export configurations
  // ---------------------------------------------------------------------------
  const parcelExportConfig = {
    title:    'Avance por Parcela',
    filename: `navabuena_avance_parcelas_${format(new Date(), 'yyyyMMdd')}`,
    columns: [
      { header: 'Parcela',           key: 'display_name',  width: 16 },
      { header: '% Reportado',       key: 'reported_pct',  width: 14 },
      { header: '% Validado',        key: 'validated_pct', width: 14 },
      { header: 'Actividades',       key: 'total',         width: 14 },
      { header: 'Finalizadas',       key: 'finalizada',    width: 14 },
      { header: 'Bloqueadas',        key: 'bloqueada',     width: 14 },
    ],
    data: (parcelData ?? []).map((p) => ({
      display_name:  p.display_name,
      reported_pct:  `${p.reported_pct}%`,
      validated_pct: `${p.validated_pct}%`,
      total:         p.total,
      finalizada:    p.finalizada,
      bloqueada:     p.bloqueada,
    })),
  }

  const disciplineExportConfig = {
    title:    'Avance por Disciplina',
    filename: `navabuena_avance_disciplinas_${format(new Date(), 'yyyyMMdd')}`,
    columns: [
      { header: 'Disciplina',   key: 'discipline',    width: 22 },
      { header: '% Reportado',  key: 'reported_pct',  width: 14 },
      { header: '% Validado',   key: 'validated_pct', width: 14 },
      { header: 'Actividades',  key: 'total',         width: 14 },
      { header: 'Finalizadas',  key: 'finalizada',    width: 14 },
      { header: 'Bloqueadas',   key: 'bloqueada',     width: 14 },
    ],
    data: (disciplineData ?? []).map((d) => {
      const info = getDisciplineInfo(d.discipline as DisciplineType)
      return {
        discipline:    info.label,
        reported_pct:  `${d.reported_pct}%`,
        validated_pct: `${d.validated_pct}%`,
        total:         d.total,
        finalizada:    d.finalizada,
        bloqueada:     d.bloqueada,
      }
    }),
  }

  const reportOptions = [
    {
      label:   'Resumen por Parcela (Excel)',
      format:  'excel' as const,
      config:  parcelExportConfig,
    },
    {
      label:   'Resumen por Parcela (PDF)',
      format:  'pdf' as const,
      config:  parcelExportConfig,
      subtitle: `Informe generado: ${today}`,
    },
    {
      label:   'Resumen por Disciplina (Excel)',
      format:  'excel' as const,
      config:  disciplineExportConfig,
    },
    {
      label:   'Resumen por Disciplina (PDF)',
      format:  'pdf' as const,
      config:  disciplineExportConfig,
      subtitle: `Informe generado: ${today}`,
    },
    {
      label:   'Informe Ejecutivo (PDF)',
      format:  'pdf' as const,
      config: {
        title:    'Informe Ejecutivo — PSFV Navabuena',
        filename: `navabuena_informe_ejecutivo_${format(new Date(), 'yyyyMMdd')}`,
        columns: [
          { header: 'Concepto', key: 'concepto', width: 30 },
          { header: 'Valor',    key: 'valor',    width: 20 },
        ],
        data: [
          { concepto: 'Avance global reportado',    valor: `${(parcelData?.reduce((s, p) => s + p.reported_pct, 0) ?? 0 / Math.max(1, parcelData?.length ?? 1)).toFixed(1)}%` },
          { concepto: 'Total actividades',          valor: parcelData?.reduce((s, p) => s + p.total, 0)     ?? 0 },
          { concepto: 'Actividades finalizadas',    valor: parcelData?.reduce((s, p) => s + p.finalizada, 0) ?? 0 },
          { concepto: 'Actividades bloqueadas',     valor: parcelData?.reduce((s, p) => s + p.bloqueada, 0)  ?? 0 },
          { concepto: 'Parcelas en seguimiento',    valor: parcelData?.length ?? 0 },
          { concepto: 'Fecha del informe',          valor: today },
        ],
      },
      subtitle: `Generado: ${today}`,
    },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Informes y Exportaciones</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Genera y descarga informes del estado del proyecto
          </p>
        </div>
        <ExportButton options={reportOptions} label="Exportar informe" />
      </div>

      {/* Quick export cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          {
            title:    'Avance por Parcela',
            desc:     'Resumen de progreso para las 26 parcelas',
            icon:     FileSpreadsheet,
            color:    'text-green-600',
            bg:       'bg-green-50',
            onExcel:  () => exportToExcel(parcelExportConfig),
            onPDF:    () => exportToPDF(parcelExportConfig, `Informe: ${today}`),
          },
          {
            title:    'Avance por Disciplina',
            desc:     'Progreso desglosado por disciplina técnica',
            icon:     FileSpreadsheet,
            color:    'text-blue-600',
            bg:       'bg-blue-50',
            onExcel:  () => exportToExcel(disciplineExportConfig),
            onPDF:    () => exportToPDF(disciplineExportConfig, `Informe: ${today}`),
          },
          {
            title:    'Informe Ejecutivo',
            desc:     'Resumen ejecutivo del estado del proyecto',
            icon:     FileText,
            color:    'text-purple-600',
            bg:       'bg-purple-50',
            onExcel:  undefined,
            onPDF:    () => exportToPDF(reportOptions[4].config as never, `Generado: ${today}`),
          },
        ].map((card) => {
          const Icon = card.icon
          return (
            <div key={card.title} className="card p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${card.bg}`}>
                  <Icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{card.title}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{card.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {card.onExcel && (
                  <button onClick={card.onExcel} className="btn-secondary text-xs flex-1 justify-center">
                    <FileSpreadsheet className="w-3.5 h-3.5 text-green-600" />
                    Excel
                  </button>
                )}
                <button onClick={card.onPDF} className="btn-secondary text-xs flex-1 justify-center">
                  <FileText className="w-3.5 h-3.5 text-red-500" />
                  PDF
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Preview: Parcel progress table */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Vista previa — Avance por Parcela</h3>
          {parcelLoading && (
            <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Parcela</th>
                <th>% Reportado</th>
                <th>% Validado</th>
                <th>Actividades</th>
                <th>Finalizadas</th>
                <th>Bloqueadas</th>
              </tr>
            </thead>
            <tbody>
              {(parcelData ?? []).map((p) => (
                <tr key={p.parcel_number} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs font-medium text-gray-800">{p.display_name}</td>
                  <td className="px-4 py-3 w-32"><ProgressCell value={p.reported_pct} /></td>
                  <td className="px-4 py-3 w-32"><ProgressCell value={p.validated_pct} /></td>
                  <td className="px-4 py-3 text-xs text-center text-gray-700">{p.total}</td>
                  <td className="px-4 py-3 text-xs text-center text-green-600 font-medium">{p.finalizada}</td>
                  <td className="px-4 py-3 text-xs text-center">
                    {p.bloqueada > 0
                      ? <span className="text-red-600 font-medium">{p.bloqueada}</span>
                      : <span className="text-gray-300">0</span>
                    }
                  </td>
                </tr>
              ))}
              {!parcelLoading && (!parcelData || parcelData.length === 0) && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-sm text-gray-400">
                    Sin datos disponibles
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
